import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_URL } from "../../../core/constants";
import { getJupiterToken } from "../../../core/cookie";
import { useEffect, useState } from "react";
import { Button, Spinner } from "@material-tailwind/react";
import BrandPill from "../BrandPill/BrandPill";
import StatusPill from "../StatusPill/StatusPill";
import BorrowingPill from "../BorrowingPill/BorrowingPill";
import dayjs from "dayjs";
import format from "format-number";
import axios from "axios";
import ReactMarkdown from "react-markdown";

const ApplicationDetail = ({ id }) => {
  const [unauthorized, setUnauthorized] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("for_review");
  const queryClient = useQueryClient();

  const markAsTest = async () => {
    return await axios.post(
      `${API_URL}/jupiter/applications/${id}/mark-as-test`,
      {},
      {
        withCredentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-jupiter-token": getJupiterToken(),
        },
      }
    );
  };

  const requestAI = async () => {
    const url = `${API_URL}/jupiter/applications/${id}/execute-ai-assessment`;
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-jupiter-token": getJupiterToken(),
      },
    });
    return response.json();
  };

  const updateStatus = async () => {
    const url = `${API_URL}/jupiter/applications/${id}/change-status`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "x-jupiter-token": getJupiterToken(),
      },
      body: JSON.stringify({
        status: selectedStatus,
      }),
    });
    return response.json();
  };

  const { isPending, error, data } = useQuery({
    refetchInterval: 10000,
    queryKey: ["application"],
    queryFn: async () => {
      const url = `${API_URL}/jupiter/applications/${id}`;
      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-jupiter-token": getJupiterToken(),
        },
      });

      if (response.status === 401) {
        console.error("Unauthorized access");
        setUnauthorized(true);
        return;
      }

      return response.json();
    },
  });

  useEffect(() => {
    if (data?.status) {
      setSelectedStatus(data.status);
    }
  }, [data?.status]);

  const mutation = useMutation({
    mutationFn: markAsTest,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application"] });
    },
  });

  const aiMutation = useMutation({
    mutationFn: requestAI,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application"] });
    },
  });

  const statusMutation = useMutation({
    mutationFn: updateStatus,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["application"] });
    },
  });

  const renderLoading = () => {
    return (
      <div className="w-full flex min-h-screen">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <Spinner className="h-12 w-12" />
          <span>One moment...</span>
        </div>
      </div>
    );
  };

  const renderError = () => {
    return (
      <div className="w-full flex min-h-screen">
        <div className="m-auto justify-center items-center flex flex-col gap-6">
          <div className="w-8">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Zm-.375 0h.008v.015h-.008V9.75Z"
              />
            </svg>
          </div>
          <span>An error has occurred, please try again.</span>
        </div>
      </div>
    );
  };

  const renderNotAuthorised = () => {
    return (
      <div className="w-full flex flex-col justify-center items-center gap-6">
        <div className="w-16 h-16 mx-auto">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
            <path d="M256 0c-25.3 0-47.2 14.7-57.6 36c-7-2.6-14.5-4-22.4-4c-35.3 0-64 28.7-64 64l0 165.5-2.7-2.7c-25-25-65.5-25-90.5 0s-25 65.5 0 90.5L106.5 437c48 48 113.1 75 181 75l8.5 0 8 0c1.5 0 3-.1 4.5-.4c91.7-6.2 165-79.4 171.1-171.1c.3-1.5 .4-3 .4-4.5l0-176c0-35.3-28.7-64-64-64c-5.5 0-10.9 .7-16 2l0-2c0-35.3-28.7-64-64-64c-7.9 0-15.4 1.4-22.4 4C303.2 14.7 281.3 0 256 0zM240 96.1l0-.1 0-32c0-8.8 7.2-16 16-16s16 7.2 16 16l0 31.9 0 .1 0 136c0 13.3 10.7 24 24 24s24-10.7 24-24l0-136c0 0 0 0 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 55.9c0 0 0 .1 0 .1l0 80c0 13.3 10.7 24 24 24s24-10.7 24-24l0-71.9c0 0 0-.1 0-.1c0-8.8 7.2-16 16-16s16 7.2 16 16l0 172.9c-.1 .6-.1 1.3-.2 1.9c-3.4 69.7-59.3 125.6-129 129c-.6 0-1.3 .1-1.9 .2l-4.9 0-8.5 0c-55.2 0-108.1-21.9-147.1-60.9L52.7 315.3c-6.2-6.2-6.2-16.4 0-22.6s16.4-6.2 22.6 0L119 336.4c6.9 6.9 17.2 8.9 26.2 5.2s14.8-12.5 14.8-22.2L160 96c0-8.8 7.2-16 16-16c8.8 0 16 7.1 16 15.9L192 232c0 13.3 10.7 24 24 24s24-10.7 24-24l0-135.9z" />
          </svg>
        </div>
        <div className="m-auto justify-center items-center flex flex-col gap-6 text-base">
          <span>You cannot view this document. You are not authorised.</span>
        </div>
      </div>
    );
  };

  if (isPending) {
    return renderLoading();
  }

  if (error) {
    return renderError();
  }

  if (unauthorized) {
    return renderNotAuthorised();
  }

  const dob = dayjs(
    `${data?.application?.applicant?.dob?.year}-${data?.application?.applicant?.dob?.month}-${data?.application?.applicant?.dob?.day}`
  ).format("DD MMMM YYYY");
  const repayment =
    data?.application?.amount / data?.application?.applicant?.term;
  const difference =
    data?.application?.iande?.income?.totalIncome -
    data?.application?.iande?.expenditure?.totalExpenditure;

  const assessmentText = data?.ai?.output_text || "No AI assessment available.";

  return (
    <>
      <section>
        <div className="flex w-full mb-16">
          <a
            href="/jupiter/dashboard"
            className="text-black text-base hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
          >
            <div className="w-4 h-4 mr-2 inline-block">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L109.2 288 416 288c17.7 0 32-14.3 32-32s-14.3-32-32-32l-306.7 0L214.6 118.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-160 160z" />
              </svg>
            </div>
            Back to dashboard
          </a>
        </div>
      </section>
      <section>
        <div className="w-full flex flex-row mb-8 border-b-2 border-gray-200 pb-4">
          <div className="basis-1/2 flex flex-col gap-2">
            <span className="text-3xl leading-4">{data?.reference || ""}</span>
          </div>

          <div className="basis-1/2 text-right">
            <span className="text-3xl leading-4">
              &pound;{data?.application?.amount || ""}
            </span>
          </div>
        </div>
      </section>

      {data?.status === "test" && (
        <section>
          <div className="bg-red-800 text-white p-4 mb-4 rounded-md">
            This has been marked as a test application. It can safely be
            ignored.
          </div>
        </section>
      )}

      <section>
        <div className="flex flex-row gap-2 mb-4 pb-8">
          <BrandPill brand={data?.application?.brand} />
          <BorrowingPill
            borrowingType={data?.application.applicant.borrowingType}
          />
        </div>
      </section>
      <section className="bg-gray-100 rounded-md p-8 pb-4">
        <div className="">
          <div className="mb-4 grid gap-4 sm:grid-cols-2 sm:gap-8 lg:gap-16">
            <div className="space-y-4">
              <div className="flex space-x-4">
                <div>
                  <h2 className="flex items-center text-xl font-bold leading-none text-gray-900 dark:text-white sm:text-2xl">
                    {data?.application?.applicant?.title || ""}{" "}
                    {data?.application?.applicant?.firstName || ""}{" "}
                    <span className="uppercase ml-1">
                      {data?.application?.applicant?.lastName || ""}
                    </span>
                  </h2>
                </div>
              </div>
              <dl className="">
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Email Address
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  <a
                    href={`mailto:${data?.application?.applicant?.email || ""}`}
                    className="hover:cursor-pointer"
                  >
                    {data?.application?.applicant?.email || ""}
                  </a>
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Home Address
                </dt>
                <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  <div className="flex flex-col gap-1">
                    <span className="block">
                      {data?.application?.applicant?.address?.line1 || ""}
                    </span>
                    <span className="block">
                      {data?.application?.applicant?.address?.line2 || ""}
                    </span>
                    <span className="block">
                      {data?.application?.applicant?.address?.city || ""}
                    </span>
                    <span className="block">
                      {data?.application?.applicant?.address?.county || ""}
                    </span>
                    <span className="uppercase">
                      {data?.application?.applicant?.address?.postcode || ""}
                    </span>
                  </div>
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Date of Birth
                </dt>
                <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  {dob || ""}
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Applied
                </dt>
                <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  {data?.created || ""}
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Age of data
                </dt>
                <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  TBC
                </dd>
              </dl>
            </div>
            <div className="space-y-4">
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Phone Number
                </dt>
                <dd className="text-gray-500 dark:text-gray-400">
                  {data?.application?.applicant?.phoneNumber || ""}
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Status
                </dt>
                <dd className="flex items-center gap-1 text-gray-500 dark:text-gray-400">
                  <StatusPill status={data?.status} />
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Borrowing Type
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 uppercase">
                  {data?.application?.applicant?.borrowingType || "not set"}
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Amount
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 uppercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.amount || 0
                  )}
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Term
                </dt>
                <dd className="text-gray-500 dark:text-gray-400">
                  Pay in {data?.application?.applicant?.term || ""} at{" "}
                  <span className="inline-bloack text-gray-500">
                    &pound;
                    {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                      repayment
                    )}{" "}
                    per payment
                  </span>
                </dd>
              </dl>
              <dl>
                <dt className="font-semibold text-gray-900 dark:text-white">
                  Referral
                </dt>
                <dd className="text-gray-500 dark:text-gray-400">
                  {data?.application?.applicant?.referral || ""}
                </dd>
              </dl>
              <dl>
                <dt className="mb-1 font-semibold text-gray-900 dark:text-white">
                  Purpose
                </dt>
                <dd className="text-gray-500 dark:text-gray-400">
                  {data?.application?.applicant?.financePurpose || ""}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="flex flex-row justify-between">
          <div className="my-4 flex gap-1">
            {data?.status !== "test" ? (
              <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending}
              >
                Mark As Test
              </Button>
            ) : null}

            <Button
              onClick={() => aiMutation.mutate()}
              disabled={aiMutation.isPending}
            >
              Request AI Assessment
            </Button>
          </div>
          <div className="flex flex-row gap-2 my-4">
            <label htmlFor="status" className="self-center pr-2">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="h-12 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
            >
              <option value="for_review">For review</option>
              <option value="no_docs">No documents provided</option>
              <option value="waiting_for_documents">
                Waiting for documents
              </option>
              <option value="waiting_for_bank_statements">
                Waiting for bank statements
              </option>
              <option value="waiting_for_payslips">Waiting for payslips</option>
              <option value="under_review_with_credit">
                Under review with credit
              </option>
              <option value="with_customer">With customer</option>
              <option value="grant_issued">Grant issued</option>
              <option value="loan_issued">Loan issued</option>
              <option value="declined">Declined</option>
              <option value="test">Test</option>
            </select>
            <Button
              onClick={() => statusMutation.mutate()}
              disabled={statusMutation.isPending}
            >
              Set
            </Button>
          </div>
        </div>
      </section>

      {data?.ai && (
        <section>
          <h2 className="text-lg font-bold leading-none text-gray-600 mb-1 mt-4 block">
            AI Assessment
          </h2>
          <div className="p-4 bg-gray-100 rounded-md text-sm">
            <ReactMarkdown>{assessmentText}</ReactMarkdown>
          </div>
          <div className="flex gap-4">
            <div className="text-sm text-gray-400">
              Tokens used: {data?.ai?.usage?.total_tokens || 0}
            </div>
            <div className="text-sm text-gray-400">
              Model used: {data?.ai?.model || "Not available"}
            </div>
            <div className="text-sm text-gray-400">
              ID: {data?.ai?.id || "Not available"}
            </div>
          </div>
        </section>
      )}

      {data?.status === "for_review" && (
        <section>
          <div className="w-full flex flex-col mb-8">
            <h2 className="text-lg font-bold leading-none text-gray-600 mb-1 mt-4 block">
              Documents
            </h2>
            <p className="text-small text-gray-500 mb-4">
              If all documents are not listed, the applicant did not provide
              them or they are in a format that cannot be displayed or read.
              Either contact the applicant to provide the documents again or
              reject this application.
            </p>
            <ul>
              {data?.documents?.principal && (
                <li className="bg-purple-200 rounded-md mb-2 text-center">
                  <a
                    href={`/jupiter/view-document?id=${data?._id || ""}&hash=${data?.hash || ""}&type=principal&mime=${data?.documents?.principal?.mimetype}`}
                    className="text-purple-800 w-full p-4 flex flex-col items-center"
                  >
                    {/* <div className="w-8 h-8 mr-2 block">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 576 512"
                      >
                        <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z" />
                      </svg>
                    </div> */}
                    Principal Document ({data?.documents?.principal?.mimetype})
                    <span className="text-xs block">
                      {data?.documents?.principal?.originalname}
                    </span>
                    <span className="text-xs block">
                      {(data?.documents?.principal?.size / 1024).toFixed(2)} KB
                    </span>
                  </a>
                </li>
              )}
              {data?.documents?.secondary && (
                <li className="bg-purple-100 rounded-md mb-2 text-center">
                  <a
                    href={`/jupiter/view-document?id=${data?._id || ""}&hash=${data?.hash || ""}&type=secondary&mime=${data?.documents?.secondary?.mimetype}`}
                    className="text-purple-800 w-full block p-4"
                  >
                    Secondary Document ({data?.documents?.secondary?.mimetype})
                    <span className="text-xs block">
                      {data?.documents?.secondary?.originalname}
                    </span>
                    <span className="text-xs block">
                      {(data?.documents?.secondary?.size / 1024).toFixed(2)} KB
                    </span>
                  </a>
                </li>
              )}
              {data?.documents?.referral && (
                <li className="bg-purple-50 rounded-md mb-2 text-center">
                  <a
                    href={`/jupiter/view-document?id=${data?._id || ""}&hash=${data?.hash || ""}&type=referral&mime=${data?.documents?.referral?.mimetype}`}
                    className="text-purple-800 w-full block p-4"
                  >
                    Referral Document ({data?.documents?.referral?.mimetype})
                    <span className="text-xs block">
                      {data?.documents?.referral?.originalname}
                    </span>
                    <span className="text-xs block">
                      {(data?.documents?.referral?.size / 1024).toFixed(2)} KB
                    </span>
                  </a>
                </li>
              )}
            </ul>
          </div>
        </section>
      )}

      <section>
        <div className="mb-4 grid gap-4 sm:grid-cols-2 border-b-2 border-gray-200 divide-gray-200 divide-x-2 divide-dashed">
          <div className="mb-8 pr-2">
            <h2 className="text-lg font-bold leading-none text-gray-600 mb-4 mt-4">
              Income
            </h2>
            <dl className="mb-4 bg-green-100 rounded-md p-4">
              <dt className="font-semibold text-gray-900 text-base">
                Total Income
              </dt>
              <dd className="text-gray-800 text-base dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.totalIncome || 0
                )}{" "}
                monthly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900 dark:text-white">
                Employment
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.employment?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.employment?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900 dark:text-white">
                ESA
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.esa?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.esa?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900 dark:text-white">
                Universal Credit
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.universalCredit?.monthly ||
                    0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.universalCredit?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900 dark:text-white">
                Child Benefit
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.childBenefit?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.childBenefit?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900 dark:text-white">
                Other Income
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.otherIncome?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.income?.otherIncome?.weekly || 0
                )}{" "}
                weekly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase italic">
                {data?.application?.iande?.income?.otherIncome?.description ||
                  ""}
              </dd>
            </dl>

            <div className="mb-8 bg-gray-100 rounded-md p-4">
              <h2 className="text-lg font-bold leading-none text-gray-600 mb-4 mt-4">
                Arrears
              </h2>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 576 512"
                    >
                      <path d="M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z" />
                    </svg>
                  </div>
                  Rent/Mortgage
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.rent || 0
                  )}
                </dd>
              </dl>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 448 512"
                    >
                      <path d="M349.4 44.6c5.9-13.7 1.5-29.7-10.6-38.5s-28.6-8-39.9 1.8l-256 224c-10 8.8-13.6 22.9-8.9 35.3S50.7 288 64 288l111.5 0L98.6 467.4c-5.9 13.7-1.5 29.7 10.6 38.5s28.6 8 39.9-1.8l256-224c10-8.8 13.6-22.9 8.9-35.3s-16.6-20.7-30-20.7l-111.5 0L349.4 44.6z" />
                    </svg>
                  </div>
                  Electricity
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.electricity || 0
                  )}
                </dd>
              </dl>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 384 512"
                    >
                      <path d="M153.6 29.9l16-21.3C173.6 3.2 180 0 186.7 0C198.4 0 208 9.6 208 21.3V43.5c0 13.1 5.4 25.7 14.9 34.7L307.6 159C356.4 205.6 384 270.2 384 337.7C384 434 306 512 209.7 512H192C86 512 0 426 0 320v-3.8c0-48.8 19.4-95.6 53.9-130.1l3.5-3.5c4.2-4.2 10-6.6 16-6.6C85.9 176 96 186.1 96 198.6V288c0 35.3 28.7 64 64 64s64-28.7 64-64v-3.9c0-18-7.2-35.3-19.9-48l-38.6-38.6c-24-24-37.5-56.7-37.5-90.7c0-27.7 9-54.8 25.6-76.9z" />
                    </svg>
                  </div>
                  Gas
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.gas || 0
                  )}
                </dd>
              </dl>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 384 512"
                    >
                      <path d="M192 512C86 512 0 426 0 320C0 228.8 130.2 57.7 166.6 11.7C172.6 4.2 181.5 0 191.1 0l1.8 0c9.6 0 18.5 4.2 24.5 11.7C253.8 57.7 384 228.8 384 320c0 106-86 192-192 192zM96 336c0-8.8-7.2-16-16-16s-16 7.2-16 16c0 61.9 50.1 112 112 112c8.8 0 16-7.2 16-16s-7.2-16-16-16c-44.2 0-80-35.8-80-80z" />
                    </svg>
                  </div>
                  Water
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.water || 0
                  )}
                </dd>
              </dl>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 384 512"
                    >
                      <path d="M14 2.2C22.5-1.7 32.5-.3 39.6 5.8L80 40.4 120.4 5.8c9-7.7 22.3-7.7 31.2 0L192 40.4 232.4 5.8c9-7.7 22.3-7.7 31.2 0L304 40.4 344.4 5.8c7.1-6.1 17.1-7.5 25.6-3.6s14 12.4 14 21.8l0 464c0 9.4-5.5 17.9-14 21.8s-18.5 2.5-25.6-3.6L304 471.6l-40.4 34.6c-9 7.7-22.3 7.7-31.2 0L192 471.6l-40.4 34.6c-9 7.7-22.3 7.7-31.2 0L80 471.6 39.6 506.2c-7.1 6.1-17.1 7.5-25.6 3.6S0 497.4 0 488L0 24C0 14.6 5.5 6.1 14 2.2zM96 144c-8.8 0-16 7.2-16 16s7.2 16 16 16l192 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L96 144zM80 352c0 8.8 7.2 16 16 16l192 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L96 336c-8.8 0-16 7.2-16 16zM96 240c-8.8 0-16 7.2-16 16s7.2 16 16 16l192 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L96 240z" />
                    </svg>
                  </div>
                  Council Tax
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.councilTax || 0
                  )}
                </dd>
              </dl>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 576 512"
                    >
                      <path d="M64 32C28.7 32 0 60.7 0 96l0 32 576 0 0-32c0-35.3-28.7-64-64-64L64 32zM576 224L0 224 0 416c0 35.3 28.7 64 64 64l448 0c35.3 0 64-28.7 64-64l0-192zM112 352l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm112 16c0-8.8 7.2-16 16-16l128 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-128 0c-8.8 0-16-7.2-16-16z" />
                    </svg>
                  </div>
                  Credit/Loans
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.credit || 0
                  )}
                </dd>
              </dl>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 320 512"
                    >
                      <path d="M0 64C0 28.7 28.7 0 64 0L256 0c35.3 0 64 28.7 64 64l0 384c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64zm64 96l0 64c0 17.7 14.3 32 32 32l128 0c17.7 0 32-14.3 32-32l0-64c0-17.7-14.3-32-32-32L96 128c-17.7 0-32 14.3-32 32zM80 352a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm56-56a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm56-56a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zM128 48c-8.8 0-16 7.2-16 16s7.2 16 16 16l64 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-64 0z" />
                    </svg>
                  </div>
                  Mobile
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.mobile || 0
                  )}
                </dd>
              </dl>
              <dl className="mb-4">
                <dt className="font-semibold text-gray-900">
                  <div className="w-4 h-4 mr-2 inline-block">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 512 512"
                    >
                      <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM169.8 165.3c7.9-22.3 29.1-37.3 52.8-37.3l58.3 0c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24l0-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1l-58.3 0c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" />
                    </svg>
                  </div>
                  Other
                </dt>
                <dd className="text-gray-500 dark:text-gray-400 lowercase">
                  &pound;
                  {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                    data?.application?.iande?.arrears?.other || 0
                  )}
                </dd>
              </dl>
            </div>
          </div>
          <div className="mb-8 pl-6">
            <h2 className="text-lg font-bold leading-none text-gray-600 mb-4 mt-4">
              Expenditure
            </h2>
            <dl className="mb-4 bg-red-100 rounded-md p-4">
              <dt className="font-semibold text-gray-900 text-base">
                Total Expenditure
              </dt>
              <dd className="text-gray-800 text-base dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.totalExpenditure || 0
                )}{" "}
                monthly
              </dd>
            </dl>
            <dl className="mb-4 bg-gray-100 rounded-md p-4">
              <dt className="font-semibold text-gray-900 text-base">
                Difference
              </dt>
              <dd className="text-gray-800 text-base dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  difference || 0
                )}{" "}
                monthly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                    <path d="M575.8 255.5c0 18-15 32.1-32 32.1l-32 0 .7 160.2c0 2.7-.2 5.4-.5 8.1l0 16.2c0 22.1-17.9 40-40 40l-16 0c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1L416 512l-24 0c-22.1 0-40-17.9-40-40l0-24 0-64c0-17.7-14.3-32-32-32l-64 0c-17.7 0-32 14.3-32 32l0 64 0 24c0 22.1-17.9 40-40 40l-24 0-31.9 0c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2l-16 0c-22.1 0-40-17.9-40-40l0-112c0-.9 0-1.9 .1-2.8l0-69.7-32 0c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z" />
                  </svg>
                </div>
                Rent/Mortgage
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.rent?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.rent?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                    <path d="M416 0C400 0 288 32 288 176l0 112c0 35.3 28.7 64 64 64l32 0 0 128c0 17.7 14.3 32 32 32s32-14.3 32-32l0-128 0-112 0-208c0-17.7-14.3-32-32-32zM64 16C64 7.8 57.9 1 49.7 .1S34.2 4.6 32.4 12.5L2.1 148.8C.7 155.1 0 161.5 0 167.9c0 45.9 35.1 83.6 80 87.7L80 480c0 17.7 14.3 32 32 32s32-14.3 32-32l0-224.4c44.9-4.1 80-41.8 80-87.7c0-6.4-.7-12.8-2.1-19.1L191.6 12.5c-1.8-8-9.3-13.3-17.4-12.4S160 7.8 160 16l0 134.2c0 5.4-4.4 9.8-9.8 9.8c-5.1 0-9.3-3.9-9.8-9L127.9 14.6C127.2 6.3 120.3 0 112 0s-15.2 6.3-15.9 14.6L83.7 151c-.5 5.1-4.7 9-9.8 9c-5.4 0-9.8-4.4-9.8-9.8L64 16zm48.3 152l-.3 0-.3 0 .3-.7 .3 .7z" />
                  </svg>
                </div>
                Food
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.food?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.food?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
                    <path d="M349.4 44.6c5.9-13.7 1.5-29.7-10.6-38.5s-28.6-8-39.9 1.8l-256 224c-10 8.8-13.6 22.9-8.9 35.3S50.7 288 64 288l111.5 0L98.6 467.4c-5.9 13.7-1.5 29.7 10.6 38.5s28.6 8 39.9-1.8l256-224c10-8.8 13.6-22.9 8.9-35.3s-16.6-20.7-30-20.7l-111.5 0L349.4 44.6z" />
                  </svg>
                </div>
                Electricity
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.electricity?.monthly ||
                    0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.electricity?.weekly ||
                    0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                    <path d="M153.6 29.9l16-21.3C173.6 3.2 180 0 186.7 0C198.4 0 208 9.6 208 21.3V43.5c0 13.1 5.4 25.7 14.9 34.7L307.6 159C356.4 205.6 384 270.2 384 337.7C384 434 306 512 209.7 512H192C86 512 0 426 0 320v-3.8c0-48.8 19.4-95.6 53.9-130.1l3.5-3.5c4.2-4.2 10-6.6 16-6.6C85.9 176 96 186.1 96 198.6V288c0 35.3 28.7 64 64 64s64-28.7 64-64v-3.9c0-18-7.2-35.3-19.9-48l-38.6-38.6c-24-24-37.5-56.7-37.5-90.7c0-27.7 9-54.8 25.6-76.9z" />
                  </svg>
                </div>
                Gas
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.gas?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.gas?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                    <path d="M192 512C86 512 0 426 0 320C0 228.8 130.2 57.7 166.6 11.7C172.6 4.2 181.5 0 191.1 0l1.8 0c9.6 0 18.5 4.2 24.5 11.7C253.8 57.7 384 228.8 384 320c0 106-86 192-192 192zM96 336c0-8.8-7.2-16-16-16s-16 7.2-16 16c0 61.9 50.1 112 112 112c8.8 0 16-7.2 16-16s-7.2-16-16-16c-44.2 0-80-35.8-80-80z" />
                  </svg>
                </div>
                Water
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.water?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.water?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                    <path d="M14 2.2C22.5-1.7 32.5-.3 39.6 5.8L80 40.4 120.4 5.8c9-7.7 22.3-7.7 31.2 0L192 40.4 232.4 5.8c9-7.7 22.3-7.7 31.2 0L304 40.4 344.4 5.8c7.1-6.1 17.1-7.5 25.6-3.6s14 12.4 14 21.8l0 464c0 9.4-5.5 17.9-14 21.8s-18.5 2.5-25.6-3.6L304 471.6l-40.4 34.6c-9 7.7-22.3 7.7-31.2 0L192 471.6l-40.4 34.6c-9 7.7-22.3 7.7-31.2 0L80 471.6 39.6 506.2c-7.1 6.1-17.1 7.5-25.6 3.6S0 497.4 0 488L0 24C0 14.6 5.5 6.1 14 2.2zM96 144c-8.8 0-16 7.2-16 16s7.2 16 16 16l192 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L96 144zM80 352c0 8.8 7.2 16 16 16l192 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L96 336c-8.8 0-16 7.2-16 16zM96 240c-8.8 0-16 7.2-16 16s7.2 16 16 16l192 0c8.8 0 16-7.2 16-16s-7.2-16-16-16L96 240z" />
                  </svg>
                </div>
                Council Tax
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.councilTax?.monthly ||
                    0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.councilTax?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
                    <path d="M0 64C0 28.7 28.7 0 64 0L256 0c35.3 0 64 28.7 64 64l0 384c0 35.3-28.7 64-64 64L64 512c-35.3 0-64-28.7-64-64L0 64zm64 96l0 64c0 17.7 14.3 32 32 32l128 0c17.7 0 32-14.3 32-32l0-64c0-17.7-14.3-32-32-32L96 128c-17.7 0-32 14.3-32 32zM80 352a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm56-56a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zm56-56a24 24 0 1 0 0-48 24 24 0 1 0 0 48zm24 56a24 24 0 1 0 -48 0 24 24 0 1 0 48 0zM128 48c-8.8 0-16 7.2-16 16s7.2 16 16 16l64 0c8.8 0 16-7.2 16-16s-7.2-16-16-16l-64 0z" />
                  </svg>
                </div>
                Mobile Phone
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.mobilePhone?.monthly ||
                    0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.mobilePhone?.weekly ||
                    0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M164.9 24.6c-7.7-18.6-28-28.5-47.4-23.2l-88 24C12.1 30.2 0 46 0 64C0 311.4 200.6 512 448 512c18 0 33.8-12.1 38.6-29.5l24-88c5.3-19.4-4.6-39.7-23.2-47.4l-96-40c-16.3-6.8-35.2-2.1-46.3 11.6L304.7 368C234.3 334.7 177.3 277.7 144 207.3L193.3 167c13.7-11.2 18.4-30 11.6-46.3l-40-96z" />
                  </svg>
                </div>
                Telephone/Landline
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.telephone?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.telephone?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
                    <path d="M54.2 202.9C123.2 136.7 216.8 96 320 96s196.8 40.7 265.8 106.9c12.8 12.2 33 11.8 45.2-.9s11.8-33-.9-45.2C549.7 79.5 440.4 32 320 32S90.3 79.5 9.8 156.7C-2.9 169-3.3 189.2 8.9 202s32.5 13.2 45.2 .9zM320 256c56.8 0 108.6 21.1 148.2 56c13.3 11.7 33.5 10.4 45.2-2.8s10.4-33.5-2.8-45.2C459.8 219.2 393 192 320 192s-139.8 27.2-190.5 72c-13.3 11.7-14.5 31.9-2.8 45.2s31.9 14.5 45.2 2.8c39.5-34.9 91.3-56 148.2-56zm64 160a64 64 0 1 0 -128 0 64 64 0 1 0 128 0z" />
                  </svg>
                </div>
                Broadband/Internet
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.broadband?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.broadband?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
                    <path d="M480 416l96 0c35.3 0 64-28.7 64-64l0-192c0-35.3-28.7-64-64-64l-96 0 0 320zM448 96L192 96l0 320 256 0 0-320zM64 96C28.7 96 0 124.7 0 160L0 352c0 35.3 28.7 64 64 64l96 0 0-320L64 96zM248 208a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zm120-24a24 24 0 1 1 0 48 24 24 0 1 1 0-48zM248 304a24 24 0 1 1 48 0 24 24 0 1 1 -48 0zm120-24a24 24 0 1 1 0 48 24 24 0 1 1 0-48z" />
                  </svg>
                </div>
                Insurance
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.insurance?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.insurance?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M135.2 117.4L109.1 192l293.8 0-26.1-74.6C372.3 104.6 360.2 96 346.6 96L165.4 96c-13.6 0-25.7 8.6-30.2 21.4zM39.6 196.8L74.8 96.3C88.3 57.8 124.6 32 165.4 32l181.2 0c40.8 0 77.1 25.8 90.6 64.3l35.2 100.5c23.2 9.6 39.6 32.5 39.6 59.2l0 144 0 48c0 17.7-14.3 32-32 32l-32 0c-17.7 0-32-14.3-32-32l0-48L96 400l0 48c0 17.7-14.3 32-32 32l-32 0c-17.7 0-32-14.3-32-32l0-48L0 256c0-26.7 16.4-49.6 39.6-59.2zM128 288a32 32 0 1 0 -64 0 32 32 0 1 0 64 0zm288 32a32 32 0 1 0 0-64 32 32 0 1 0 0 64z" />
                  </svg>
                </div>
                Transport
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.transport?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.transport?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
                    <path d="M64 32C28.7 32 0 60.7 0 96l0 32 576 0 0-32c0-35.3-28.7-64-64-64L64 32zM576 224L0 224 0 416c0 35.3 28.7 64 64 64l448 0c35.3 0 64-28.7 64-64l0-192zM112 352l64 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-64 0c-8.8 0-16-7.2-16-16s7.2-16 16-16zm112 16c0-8.8 7.2-16 16-16l128 0c8.8 0 16 7.2 16 16s-7.2 16-16 16l-128 0c-8.8 0-16-7.2-16-16z" />
                  </svg>
                </div>
                Credit Cards/Loans
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.credit?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.credit?.weekly || 0
                )}{" "}
                weekly
              </dd>
            </dl>
            <dl className="mb-4">
              <dt className="font-semibold text-gray-900">
                <div className="w-4 h-4 mr-2 inline-block">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM169.8 165.3c7.9-22.3 29.1-37.3 52.8-37.3l58.3 0c34.9 0 63.1 28.3 63.1 63.1c0 22.6-12.1 43.5-31.7 54.8L280 264.4c-.2 13-10.9 23.6-24 23.6c-13.3 0-24-10.7-24-24l0-13.5c0-8.6 4.6-16.5 12.1-20.8l44.3-25.4c4.7-2.7 7.6-7.7 7.6-13.1c0-8.4-6.8-15.1-15.1-15.1l-58.3 0c-3.4 0-6.4 2.1-7.5 5.3l-.4 1.2c-4.4 12.5-18.2 19-30.6 14.6s-19-18.2-14.6-30.6l.4-1.2zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z" />
                  </svg>
                </div>
                Other
              </dt>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.other?.monthly || 0
                )}{" "}
                monthly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase">
                &pound;
                {format({ truncate: 2, noSeperator: true, padRight: 2 })(
                  data?.application?.iande?.expenditure?.other?.weekly || 0
                )}{" "}
                weekly
              </dd>
              <dd className="text-gray-500 dark:text-gray-400 lowercase italic">
                {data?.application?.iande?.expenditure?.other?.description ||
                  ""}
              </dd>
            </dl>
          </div>
        </div>
      </section>
    </>
  );
};

export default ApplicationDetail;
